import React, { useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  emptyOphthalmologyRefraction,
  refractionHasContent,
  type OphthalmologyRefractionValues,
} from './ophthalmologyRefractionConstants';

const HEADER_BG = '#cfe8fc';
const CELL_BG = '#dbeafe';

interface OphthalmologyRefractionSectionProps {
  values: OphthalmologyRefractionValues;
  onChange: (next: OphthalmologyRefractionValues) => void;
  onCommit: () => void;
}

const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: CELL_BG,
    fontWeight: 700,
    fontSize: '0.9rem',
    '& fieldset': { borderColor: '#93c5fd' },
    '&:hover fieldset': { borderColor: '#3b82f6' },
    '&.Mui-focused fieldset': { borderColor: '#2563eb' },
  },
  '& input': { textAlign: 'center', py: 1 },
};

/** Ordem de TAB entre as caixinhas (grade → adição → observações). */
const TAB_FIELD_ORDER: (keyof OphthalmologyRefractionValues)[] = [
  'odSph',
  'odCyl',
  'odAxis',
  'osSph',
  'osCyl',
  'osAxis',
  'nearAdd',
  'notes',
];

const OphthalmologyRefractionSection: React.FC<OphthalmologyRefractionSectionProps> = ({
  values,
  onChange,
  onCommit,
}) => {
  const fieldRefs = useRef<Partial<Record<keyof OphthalmologyRefractionValues, HTMLInputElement | HTMLTextAreaElement>>>(
    {},
  );
  const commitButtonRef = useRef<HTMLButtonElement>(null);

  const focusField = useCallback((field: keyof OphthalmologyRefractionValues) => {
    fieldRefs.current[field]?.focus();
    fieldRefs.current[field]?.select?.();
  }, []);

  const handleFieldTab = useCallback(
    (field: keyof OphthalmologyRefractionValues, e: React.KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const idx = TAB_FIELD_ORDER.indexOf(field);
      if (idx === -1) return;

      if (e.shiftKey) {
        if (idx > 0) {
          e.preventDefault();
          focusField(TAB_FIELD_ORDER[idx - 1]);
        }
        return;
      }

      if (idx < TAB_FIELD_ORDER.length - 1) {
        e.preventDefault();
        focusField(TAB_FIELD_ORDER[idx + 1]);
        return;
      }

      e.preventDefault();
      commitButtonRef.current?.focus();
    },
    [focusField],
  );

  const bindField = (field: keyof OphthalmologyRefractionValues, ariaLabel: string) => ({
    inputRef: (el: HTMLInputElement | HTMLTextAreaElement | null) => {
      if (el) fieldRefs.current[field] = el;
    },
    onKeyDown: (e: React.KeyboardEvent) => handleFieldTab(field, e),
    inputProps: { 'aria-label': ariaLabel },
  });
  const set = (field: keyof OphthalmologyRefractionValues, value: string) => {
    onChange({ ...values, [field]: value });
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: '#475569', mb: 1.5 }}>
        Preencha o grau de óculos (esférico, cilíndrico e eixo) e as observações. O documento entra na fila
        com os demais impressos da evolução.
      </Typography>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2, mb: 2, borderColor: '#cbd5e1' }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: HEADER_BG }}>
              <TableCell colSpan={4} sx={{ fontWeight: 800, color: '#0f172a', py: 1 }}>
                Para longe.
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: HEADER_BG }}>
              <TableCell sx={{ fontWeight: 700, color: '#0f172a', width: '28%' }} />
              <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '24%' }}>
                Esférico
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '24%' }}>
                Cilíndrico
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: '#0f172a', width: '24%' }}>
                Eixo
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Olho Direito</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="+0,00"
                  value={values.odSph}
                  onChange={(e) => set('odSph', e.target.value)}
                  sx={cellInputSx}
                  {...bindField('odSph', 'Esférico olho direito')}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="−0,00"
                  value={values.odCyl}
                  onChange={(e) => set('odCyl', e.target.value)}
                  sx={cellInputSx}
                  {...bindField('odCyl', 'Cilíndrico olho direito')}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="°"
                  value={values.odAxis}
                  onChange={(e) => set('odAxis', e.target.value)}
                  sx={cellInputSx}
                  {...bindField('odAxis', 'Eixo olho direito')}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, color: '#334155' }}>Olho Esquerdo</TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="+0,00"
                  value={values.osSph}
                  onChange={(e) => set('osSph', e.target.value)}
                  sx={cellInputSx}
                  {...bindField('osSph', 'Esférico olho esquerdo')}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="−0,00"
                  value={values.osCyl}
                  onChange={(e) => set('osCyl', e.target.value)}
                  sx={cellInputSx}
                  {...bindField('osCyl', 'Cilíndrico olho esquerdo')}
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="°"
                  value={values.osAxis}
                  onChange={(e) => set('osAxis', e.target.value)}
                  sx={cellInputSx}
                  {...bindField('osAxis', 'Eixo olho esquerdo')}
                />
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: HEADER_BG }}>
              <TableCell colSpan={4} sx={{ fontWeight: 700, color: '#0f172a', py: 1.25 }}>
                Para perto: adicionar em refração de ambos os olhos no valor esférico de:
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} sx={{ borderTop: 'none', pt: 1.25, pb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    size="small"
                    placeholder="+0,00"
                    value={values.nearAdd}
                    onChange={(e) => set('nearAdd', e.target.value)}
                    sx={{ ...cellInputSx, width: { xs: '100%', sm: 180 } }}
                    {...bindField('nearAdd', 'Adição para perto')}
                  />
                </Box>
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: HEADER_BG }}>
              <TableCell colSpan={4} sx={{ fontWeight: 700, color: '#0f172a', py: 1 }}>
                Observações
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={4} sx={{ borderTop: 'none', pt: 0, pb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={3}
                  placeholder="1. Medir DNP"
                  value={values.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  {...bindField('notes', 'Observações')}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button
          ref={commitButtonRef}
          variant="contained"
          disabled={!refractionHasContent(values)}
          onClick={onCommit}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
          }}
        >
          Adicionar prescrição à fila
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onChange(emptyOphthalmologyRefraction())}
          sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#475569' }}
        >
          Limpar
        </Button>
      </Box>
    </Box>
  );
};

export default OphthalmologyRefractionSection;
